/**
 * Which layout a Drop renders as. ONE answer, from ONE field: the POV.
 *
 * THE RULE: the POV sets the design. Everything else is content.
 *
 * This was three signals read in different places and never reconciled — `pov`
 * (what the operator picked at create), `dropType` (what the hero branch
 * actually switched on) and the presence of `release` (what the publisher
 * layout switched on). Choosing "Publisher" set `pov`, left `dropType` as
 * something that is not CREATOR, and wrote no `release`, so the page fell
 * through both branches and rendered the HUB layout. The operator's choice was
 * stored correctly and then ignored by everything downstream.
 *
 * `release` is NOT a layout signal. It is dynamic information about the title —
 * whether it is pre-launch (countdown, pre-order, trailer) or post-launch — and
 * a publisher page is a publisher page either way, with or without one filled
 * in. `dropType` is consulted only for Drops that predate the persisted POV.
 */

export type DropLayout = "PUBLISHER" | "CREATOR";

interface LayoutSignals {
    pov?: string;
    dropType?: string;
}

/**
 * A roster-based POV puts a title or an organisation on the page rather than a
 * person, and they all compose the same way: one editorial scroll led by what
 * the Drop is about, with the roster as the centrepiece.
 */
/*
 * GAME was missing from this set, so a GAME Drop fell through to the CREATOR
 * layout — a title's page rendering as a person's page. `DropType` describes
 * GAME as "a single title's page, distinct from its publisher's", which is a
 * roster-shaped surface by that definition. Added 2026-08-23.
 *
 * ESPORTS became ESPORTS_PROFESSIONAL and ESPORTS_COLLEGIATE the same day. Both
 * compose the same way here — the difference between them is what may be
 * published about a person, not how the page is laid out.
 */
const PUBLISHER_LAYOUT_POVS = new Set([
    "PUBLISHER",
    "FRANCHISE",
    "GAME",
    "BRAND",
    "COMMUNITY",
    "ESPORTS_PROFESSIONAL",
    "ESPORTS_COLLEGIATE",
    "AGENCY",
]);

/*
 * "HUB" IS GONE. Hub was deleted on `epic/engagement-delivery`, so a layout
 * value naming it could only ever resolve to something that no longer exists.
 * It was never read by name either — every consumer asks `=== "PUBLISHER"`, so
 * "HUB" silently meant "not publisher" and quietly sent pre-POV Drops down the
 * creator tree.
 *
 * The fallback now answers the same question the POV branch does, from
 * `dropType`, so a PUBLISHER Drop that predates the persisted POV gets the
 * publisher layout instead of a creator page.
 */
export function dropLayout(drop: LayoutSignals): DropLayout {
    const who = drop.pov || drop.dropType;
    return who && PUBLISHER_LAYOUT_POVS.has(who) ? "PUBLISHER" : "CREATOR";
}
